/**
 * InvoiceFormModals
 *
 * All modals/dialogs used in the invoice form.
 */

import type { Address, Party } from '@/gen/proto'

import {
  BillingAddressSelectorModal,
  ShippingAddressSelectorModal,
  ValidationErrorModal,
  handleAddAddress,
} from './InvoiceFormModals-parts'
import { QuickCustomerForm, type QuickCustomerData } from '../../party/QuickCustomerForm'
import { AddressModal } from '../AddressModal'
import { InvoiceVersionHistory } from '../InvoiceVersionHistory'

import type { InvoiceFormProps } from './types'
import type { UseInvoiceFormReturn } from './useInvoiceForm'

interface InvoiceFormModalsProps {
  form: UseInvoiceFormReturn
  initialDataId: string | undefined
  sellerDetails: InvoiceFormProps['sellerDetails']
  isVendor: boolean
  onPartyUpdate?: () => void
  onQuickCreateCustomer: (data: QuickCustomerData) => Promise<Party>
  onPartyCreated: (newParty: Party) => void
}

interface AddressModalsProps {
  form: UseInvoiceFormReturn
  sellerDetails: InvoiceFormProps['sellerDetails']
  onPartyUpdate?: () => void
}

function AddBillingAddressModals({ form, sellerDetails, onPartyUpdate }: AddressModalsProps): JSX.Element {
  const { invoiceType, isShipToModalOpen, setIsShipToModalOpen, isConsigneeModalOpen, setIsConsigneeModalOpen } = form
  return (
    <>
      <AddressModal
        defaultType="billing"
        isOpen={isShipToModalOpen}
        onClose={() => setIsShipToModalOpen(false)}
        onSave={(address) => {
          void handleAddAddress(address as Address, 'billing', form, onPartyUpdate, sellerDetails.orgId)
          setIsShipToModalOpen(false)
        }}
      />
      <AddressModal
        defaultType={invoiceType === 'export' ? 'consignee' : 'shipping'}
        isOpen={isConsigneeModalOpen}
        onClose={() => setIsConsigneeModalOpen(false)}
        onSave={(address) => {
          void handleAddAddress(
            address as Address,
            invoiceType === 'export' ? 'consignee' : 'shipping',
            form,
            onPartyUpdate,
            sellerDetails.orgId,
          )
          setIsConsigneeModalOpen(false)
        }}
      />
    </>
  )
}

interface SelectorModalsProps {
  form: UseInvoiceFormReturn
  isVendor: boolean
}

function AddressSelectorModals({ form, isVendor }: SelectorModalsProps): JSX.Element {
  const f = form
  return (
    <>
      <BillingAddressSelectorModal
        allPartyAddresses={f.allPartyAddresses}
        isOpen={f.isBillingAddressSelectorOpen}
        isVendor={isVendor}
        selectedShipToAddress={f.selectedShipToAddress}
        onAddNew={() => {
          f.setIsBillingAddressSelectorOpen(false)
          f.setIsShipToModalOpen(true)
        }}
        onClose={() => f.setIsBillingAddressSelectorOpen(false)}
        onSelect={(address) => {
          f.setSelectedShipToAddress(address)
          f.setShipToType('same')
          f.setIsBillingAddressSelectorOpen(false)
        }}
      />
      <ShippingAddressSelectorModal
        consigneeType={f.consigneeType}
        invoiceType={f.invoiceType}
        isOpen={f.isShippingAddressSelectorOpen}
        isVendor={isVendor}
        selectedConsigneeAddress={f.selectedConsigneeAddress}
        selectedShipToAddress={f.selectedShipToAddress}
        shippingAddressOptions={f.shippingAddressOptions}
        onAddNew={() => {
          f.setIsShippingAddressSelectorOpen(false)
          f.setIsConsigneeModalOpen(true)
        }}
        onClose={() => f.setIsShippingAddressSelectorOpen(false)}
        onSelectParty={(address) => {
          f.setSelectedConsigneeAddress(address)
          f.setConsigneeType('party')
          f.setIsShippingAddressSelectorOpen(false)
        }}
        onSelectSame={() => {
          f.setSelectedConsigneeAddress(f.selectedShipToAddress)
          f.setConsigneeType('same')
          f.setIsShippingAddressSelectorOpen(false)
        }}
      />
    </>
  )
}

export function InvoiceFormModals(props: InvoiceFormModalsProps): JSX.Element {
  const { form, initialDataId, sellerDetails, isVendor, onPartyUpdate, onQuickCreateCustomer, onPartyCreated } = props
  const f = form
  return (
    <>
      <AddBillingAddressModals form={form} sellerDetails={sellerDetails} onPartyUpdate={onPartyUpdate} />
      <AddressSelectorModals form={form} isVendor={isVendor} />
      <QuickCustomerForm
        isOpen={f.isQuickCustomerFormOpen}
        ledgerId={sellerDetails.ledgerId ?? ''}
        mode={isVendor ? 'supplier' : 'customer'}
        orgId={sellerDetails.orgId ?? ''}
        onClose={() => f.setIsQuickCustomerFormOpen(false)}
        onCreate={(newCustomer) => {
          onPartyCreated(newCustomer)
          f.setIsQuickCustomerFormOpen(false)
        }}
        onSubmit={onQuickCreateCustomer}
      />
      {initialDataId && sellerDetails.orgId && (
        <InvoiceVersionHistory
          invoiceId={initialDataId}
          isOpen={f.isVersionHistoryVisible}
          orgId={sellerDetails.orgId}
          onClose={() => { f.setIsVersionHistoryVisible(false) }}
        />
      )}
      <ValidationErrorModal
        errors={f.errors}
        isOpen={f.isErrorModalVisible}
        onClose={() => f.setIsErrorModalVisible(false)}
      />
    </>
  )
}
